import { describeGatewayError } from "@/lib/gateway-error";

export {
	getModelImageConfig,
	GPT_IMAGE_SIZES,
	type AspectRatio,
} from "@llmgateway/shared";

export interface GeneratedImage {
	base64: string;
	mediaType: string;
}

// Freshly generated images arrive inline as base64. Saved history images are
// addressed by URL so the page only ever downloads a downscaled preview until
// the original is zoomed, downloaded or reused as a reference.
export type GalleryImage =
	| { kind: "inline"; base64: string; mediaType: string }
	| { kind: "remote"; previewUrl: string; fullUrl: string };

export function inlineImage(image: GeneratedImage): GalleryImage {
	return { kind: "inline", base64: image.base64, mediaType: image.mediaType };
}

export function inlineImageFromDataUrl(input: {
	dataUrl: string;
	mediaType: string;
}): GalleryImage {
	const comma = input.dataUrl.indexOf(",");
	return {
		kind: "inline",
		base64: comma >= 0 ? input.dataUrl.slice(comma + 1) : input.dataUrl,
		mediaType: input.mediaType,
	};
}

export function historyImage(
	apiUrl: string,
	itemId: string,
	modelIndex: number,
	imageIndex: number,
): GalleryImage {
	const base = `${apiUrl}/playground/image-history/${itemId}/images/${modelIndex}/${imageIndex}`;
	return {
		kind: "remote",
		previewUrl: `${base}?variant=preview`,
		fullUrl: base,
	};
}

export function historyInputImage(
	apiUrl: string,
	itemId: string,
	index: number,
): GalleryImage {
	const base = `${apiUrl}/playground/image-history/${itemId}/input-images/${index}`;
	return {
		kind: "remote",
		previewUrl: `${base}?variant=thumbnail`,
		fullUrl: base,
	};
}

export function toDataUrl(image: GeneratedImage): string {
	return `data:${image.mediaType};base64,${image.base64}`;
}

export interface GalleryItem {
	id: string;
	prompt: string;
	timestamp: number;
	// Organization context active when the generation was started. Captured up
	// front so the saved item is attributed to the right org even if the user
	// switches organizations while the generation is in flight.
	organizationId?: string;
	inputImages?: GalleryImage[];
	// Sidebar thumbnail URL for items loaded from the history list; freshly
	// generated items derive their thumbnail from the first inline image.
	thumbnailUrl?: string | null;
	models: {
		modelId: string;
		modelName: string;
		images: GalleryImage[];
		// Number of images still expected while a generation is in flight.
		imageCount?: number;
		error?: string;
		isLoading: boolean;
	}[];
}

export async function parseImageStream(
	response: Response,
): Promise<GeneratedImage[]> {
	const images: GeneratedImage[] = [];
	const reader = response.body?.getReader();
	if (!reader) {
		throw new Error("No response body");
	}

	const decoder = new TextDecoder();
	let buffer = "";

	while (true) {
		const { done, value } = await reader.read();
		if (done) {
			break;
		}

		buffer += decoder.decode(value, { stream: true });
		const lines = buffer.split("\n");
		buffer = lines.pop() ?? "";

		for (const line of lines) {
			const trimmed = line.trim();
			if (!trimmed) {
				continue;
			}

			// AI SDK stream protocol: lines are prefixed with "0:", "2:", etc.
			const colonIndex = trimmed.indexOf(":");
			if (colonIndex < 0) {
				continue;
			}

			const jsonStr = trimmed.slice(colonIndex + 1);
			try {
				const event = JSON.parse(jsonStr);
				if (event.type === "file" && event.url && event.mediaType) {
					const comma = event.url.indexOf(",");
					const base64 = comma >= 0 ? event.url.slice(comma + 1) : "";
					if (base64) {
						images.push({
							base64,
							mediaType: event.mediaType,
						});
					}
				}
			} catch {
				// skip non-JSON lines
			}
		}
	}

	// Process remaining buffer
	if (buffer.trim()) {
		const colonIndex = buffer.indexOf(":");
		if (colonIndex >= 0) {
			try {
				const event = JSON.parse(buffer.slice(colonIndex + 1));
				if (event.type === "file" && event.url && event.mediaType) {
					const comma = event.url.indexOf(",");
					const base64 = comma >= 0 ? event.url.slice(comma + 1) : "";
					if (base64) {
						images.push({
							base64,
							mediaType: event.mediaType,
						});
					}
				}
			} catch {
				// skip
			}
		}
	}

	return images;
}

export async function streamImageParts(
	response: Response,
	onImage: (image: GeneratedImage) => void,
): Promise<void> {
	const reader = response.body?.getReader();
	if (!reader) {
		throw new Error("No response body");
	}

	const decoder = new TextDecoder();
	let buffer = "";

	while (true) {
		const { done, value } = await reader.read();
		if (done) {
			break;
		}

		buffer += decoder.decode(value, { stream: true });
		const lines = buffer.split("\n");
		buffer = lines.pop() ?? "";

		for (const line of lines) {
			const trimmed = line.trim();
			if (!trimmed) {
				continue;
			}

			const colonIndex = trimmed.indexOf(":");
			if (colonIndex < 0) {
				continue;
			}

			const jsonStr = trimmed.slice(colonIndex + 1);
			try {
				const event = JSON.parse(jsonStr);
				if (event.type === "file" && event.url && event.mediaType) {
					const comma = event.url.indexOf(",");
					const base64 = comma >= 0 ? event.url.slice(comma + 1) : "";
					if (base64) {
						onImage({ base64, mediaType: event.mediaType });
					}
				}
			} catch {
				// skip non-JSON lines
			}
		}
	}

	if (buffer.trim()) {
		const colonIndex = buffer.indexOf(":");
		if (colonIndex >= 0) {
			try {
				const event = JSON.parse(buffer.slice(colonIndex + 1));
				if (event.type === "file" && event.url && event.mediaType) {
					const comma = event.url.indexOf(",");
					const base64 = comma >= 0 ? event.url.slice(comma + 1) : "";
					if (base64) {
						onImage({ base64, mediaType: event.mediaType });
					}
				}
			} catch {
				// skip
			}
		}
	}
}

export class ImageGenerationError extends Error {
	public status: number;

	public constructor(message: string, status: number) {
		super(message);
		this.status = status;
	}
}

/**
 * Derive a user-facing message and HTTP status from an image generation
 * failure, falling back to a generation-specific message.
 */
export function describeImageGenerationError(error: unknown): {
	message: string;
	status: number;
} {
	return describeGatewayError(error, "Image generation failed");
}

/**
 * Consume the /api/image newline-delimited JSON response. The route streams
 * `{"ping":1}` keepalives while the model works (image generation can take
 * minutes, past proxy idle timeouts), then a final `{"images":[...]}` or
 * `{"error":"...","status":4xx}` object. Throws ImageGenerationError so
 * callers can inspect the in-band status code.
 */
export async function readImageGenerationResponse(
	response: Response,
): Promise<GeneratedImage[]> {
	if (!response.ok) {
		// Pre-stream failures (auth, validation) are plain JSON with a real
		// HTTP status.
		const errorData = (await response.json().catch(() => null)) as {
			error?: string;
		} | null;
		throw new ImageGenerationError(
			errorData?.error ?? `HTTP ${response.status}: ${response.statusText}`,
			response.status,
		);
	}

	const reader = response.body?.getReader();
	if (!reader) {
		throw new ImageGenerationError("No response body", 500);
	}

	interface ImageGenerationPayload {
		images?: GeneratedImage[];
		error?: string;
		status?: number;
	}

	const decoder = new TextDecoder();
	let buffer = "";
	let result: ImageGenerationPayload | null = null;

	// Parse one NDJSON line; returns the payload object or null for blank,
	// malformed, or keepalive-ping lines.
	const parseLine = (line: string): ImageGenerationPayload | null => {
		const trimmed = line.trim();
		if (!trimmed) {
			return null;
		}
		try {
			const event = JSON.parse(trimmed);
			if (event && typeof event === "object" && !("ping" in event)) {
				return event as ImageGenerationPayload;
			}
		} catch {
			// skip malformed lines
		}
		return null;
	};

	while (true) {
		const { done, value } = await reader.read();
		if (done) {
			break;
		}
		buffer += decoder.decode(value, { stream: true });
		const lines = buffer.split("\n");
		buffer = lines.pop() ?? "";
		for (const line of lines) {
			result = parseLine(line) ?? result;
		}
	}
	// Flush any UTF-8 sequence split across the final chunk boundary.
	buffer += decoder.decode();
	result = parseLine(buffer) ?? result;

	if (result?.error) {
		throw new ImageGenerationError(result.error, result.status ?? 500);
	}
	if (!result?.images) {
		throw new ImageGenerationError(
			"The connection was interrupted before the image arrived. Check your activity history — the image may still have been generated.",
			500,
		);
	}
	return result.images;
}
